import { useEffect, useState } from "react";
import { Boxes, Download, Scale, TrendingUp, Car, BarChart3 } from "lucide-react";
import api, { fmtDate, fmtIDR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Money, EmptyState, TypeBadge, PeriodFilter, periodRange, defaultPeriod, StatusBadge } from "@/components/common";
import { PAYMENT_TYPES, STATUS, can } from "@/lib/labels";
import { downloadCsv } from "@/lib/exportCsv";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Analisa, { DateRangeBar, ExportButtons, presetRange } from "@/pages/Analisa";

function PrintHeader({ subtitle, period }) {
  return (
    <div className="flex items-center gap-3 mb-2 pb-2 border-b-2 border-zinc-800">
      <img src="/logo.jpg" alt="ForzaMobil.id" className="w-10 h-10 rounded-full object-cover" />
      <div className="flex-1">
        <h2 className="text-lg font-bold leading-none">FORZAMOBIL.ID</h2>
        <p className="text-[10px] uppercase tracking-[0.2em] mt-0.5">{subtitle}</p>
      </div>
      <p className="text-xs">{period || new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
    </div>
  );
}

const pTh = "border border-zinc-400 px-1.5 py-1";

function Row({ label, value, bold, indent, negative, testid }) {
  return (
    <div className={`flex items-center justify-between py-2.5 border-b border-[#1A1A22]/60 last:border-0 ${bold ? "bg-[#050505]/60 -mx-4 px-4" : ""}`}>
      <span className={`text-sm ${bold ? "font-semibold text-zinc-100" : "text-zinc-400"} ${indent ? "pl-4" : ""}`}>{label}</span>
      <Money data-testid={testid} value={value} className={`${bold ? "font-semibold text-base" : ""} ${negative ? "text-rose-500" : bold ? "text-blue-500" : "text-zinc-200"}`} />
    </div>
  );
}

function LabaRugi({ branch }) {
  const [[s0, e0]] = useState(() => presetRange("bulan-ini"));
  const [start, setStart] = useState(s0);
  const [end, setEnd] = useState(e0);
  const [data, setData] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams({ start, end });
    if (branch !== "all") q.set("branch_id", branch);
    api.get(`/reports/laba-rugi?${q}`).then((r) => setData(r.data));
  }, [start, end, branch]);

  const exportExcel = () => downloadCsv(`laba-rugi-${start}_${end}.csv`, ["Pos", "Jumlah"], [
    ["Pendapatan Penjualan", data.pendapatan], ["Harga Pokok Penjualan (HPP)", -data.hpp], ["Laba Kotor", data.laba_kotor],
    ["Beban Penjualan (Komisi)", -data.beban_penjualan], ["Beban Operasional", -data.beban_operasional], ["LABA BERSIH", data.laba_bersih], [],
    ["RINCIAN PENDAPATAN", ""], ...data.pendapatan_detail.map((d) => [`${d.car_name} — ${d.buyer} (${d.date})`, d.price]), [],
    ["RINCIAN BEBAN OPERASIONAL", ""], ...data.op_detail.map((d) => [`${d.category} — ${d.branch_name} (${d.date})${d.description ? ` · ${d.description}` : ""}`, d.amount]),
  ]);

  const lrRows = data ? [["Pendapatan Penjualan", data.pendapatan], ["Harga Pokok Penjualan (HPP)", -data.hpp], ["Laba Kotor", data.laba_kotor, true],
    ["Beban Penjualan (Komisi)", -data.beban_penjualan], ["Beban Operasional", -data.beban_operasional], ["LABA BERSIH", data.laba_bersih, true]] : [];

  return (
    <div>
      <DateRangeBar start={start} end={end} setStart={setStart} setEnd={setEnd} prefix="lr">
        <ExportButtons prefix="lr" onExcel={exportExcel} disabled={!data} />
      </DateRangeBar>
      {data && (
        <div className="hidden print:block print-area" data-testid="laba-rugi-print">
          <PrintHeader subtitle="Laporan Laba Rugi" period={`${fmtDate(start)} — ${fmtDate(end)}`} />
          <table className="w-full text-[11px] border-collapse mb-3">
            <tbody>{lrRows.map(([l, v, b]) => <tr key={l}><td className={`${pTh} ${b ? "font-bold" : ""}`}>{l}</td><td className={`${pTh} text-right ${b ? "font-bold" : ""}`}>{fmtIDR(v)}</td></tr>)}</tbody>
          </table>
          {data.pendapatan_detail.length > 0 && (
            <table className="w-full text-[11px] border-collapse mb-3">
              <thead><tr><th className={`${pTh} text-left`} colSpan={3}>Rincian Pendapatan</th></tr></thead>
              <tbody>{data.pendapatan_detail.map((d, i) => <tr key={i}><td className={pTh}>{d.car_name}</td><td className={pTh}>{d.buyer} · {fmtDate(d.date)}</td><td className={`${pTh} text-right`}>{fmtIDR(d.price)}</td></tr>)}</tbody>
            </table>
          )}
          {data.op_detail.length > 0 && (
            <table className="w-full text-[11px] border-collapse">
              <thead><tr><th className={`${pTh} text-left`} colSpan={3}>Rincian Beban Operasional</th></tr></thead>
              <tbody>{data.op_detail.map((d, i) => <tr key={i}><td className={pTh}>{d.category}</td><td className={pTh}>{d.branch_name} · {fmtDate(d.date)}{d.description ? ` · ${d.description}` : ""}</td><td className={`${pTh} text-right`}>{fmtIDR(d.amount)}</td></tr>)}</tbody>
            </table>
          )}
        </div>
      )}
      {!data ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : (
        <div className="print-hide grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4 fade-up" data-testid="laba-rugi-statement">
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-4">
              Laporan Laba Rugi · {fmtDate(start)} — {fmtDate(end)}
            </p>
            <Row label="Pendapatan Penjualan" value={data.pendapatan} testid="lr-pendapatan" />
            <Row label="Harga Pokok Penjualan (HPP)" value={-data.hpp} negative testid="lr-hpp" />
            <Row label="Laba Kotor" value={data.laba_kotor} bold testid="lr-laba-kotor" />
            <Row label="Beban Penjualan (Komisi)" value={-data.beban_penjualan} negative testid="lr-beban-penjualan" />
            <Row label="Beban Operasional" value={-data.beban_operasional} negative testid="lr-beban-operasional" />
            <div className="flex items-center justify-between py-3 mt-2 border-t-2 border-blue-500/40">
              <span className="font-heading font-extrabold text-zinc-50">LABA BERSIH</span>
              <Money value={data.laba_bersih} className={`text-xl font-semibold ${data.laba_bersih >= 0 ? "text-emerald-500" : "text-rose-500"}`} />
            </div>
          </div>

          <div className="lg:col-span-3 space-y-4">
            <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md fade-up">
              <p className="p-4 border-b border-[#1A1A22] text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Rincian Pendapatan ({data.pendapatan_detail.length})</p>
              <div className="max-h-52 overflow-y-auto">
                {data.pendapatan_detail.length === 0 ? <p className="p-4 text-sm text-zinc-500">Tidak ada penjualan pada periode ini.</p> : (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.pendapatan_detail.map((d, i) => (
                        <tr key={i} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                          <td className="px-4 py-2.5 text-zinc-100 font-medium">{d.car_name}<span className="block text-xs text-zinc-500">{d.buyer} · {fmtDate(d.date)}</span></td>
                          <td className="px-4 py-2.5"><TypeBadge label={PAYMENT_TYPES[d.payment_type]} /></td>
                          <td className="px-4 py-2.5 text-right"><Money value={d.price} className="text-zinc-100" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md fade-up">
              <p className="p-4 border-b border-[#1A1A22] text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Rincian Beban Operasional ({data.op_detail.length})</p>
              <div className="max-h-52 overflow-y-auto">
                {data.op_detail.length === 0 ? <p className="p-4 text-sm text-zinc-500">Tidak ada biaya operasional pada periode ini.</p> : (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.op_detail.map((d, i) => (
                        <tr key={i} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                          <td className="px-4 py-2.5 text-zinc-100">{d.category}<span className="block text-xs text-zinc-500">{d.branch_name} · {fmtDate(d.date)}</span></td>
                          <td className="px-4 py-2.5 text-zinc-400 text-xs">{d.description}</td>
                          <td className="px-4 py-2.5 text-right"><Money value={d.amount} className="text-zinc-100" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Neraca({ branch }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(defaultPeriod());
  useEffect(() => {
    const params = new URLSearchParams();
    if (branch !== "all") params.set("branch_id", branch);
    const { start, end } = periodRange(period);
    if (start) {
      params.set("start", start);
      params.set("end", end);
    }
    api.get(`/reports/neraca?${params}`).then((r) => setData(r.data));
  }, [branch, period]);

  if (!data) return <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />;

  return (
    <div>
      <div className="mb-5"><PeriodFilter value={period} onChange={setPeriod} testidPrefix="neraca" /></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="neraca-statement">
      <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4 fade-up">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-blue-500 mb-4">Aset</p>
        <Row label="Kas" value={data.aset.kas} indent testid="neraca-kas" />
        <Row label="Persediaan Mobil (HPP)" value={data.aset.persediaan} indent testid="neraca-persediaan" />
        <Row label="Piutang Usaha (Sisa DP)" value={data.aset.piutang} indent testid="neraca-piutang" />
        <div className="flex items-center justify-between py-3 mt-2 border-t-2 border-blue-500/40">
          <span className="font-heading font-extrabold text-zinc-50">TOTAL ASET</span>
          <Money value={data.aset.total} className="text-lg font-semibold text-blue-500" />
        </div>
      </div>
      <div className="space-y-4">
        <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4 fade-up" style={{ animationDelay: "80ms" }}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-rose-500 mb-4">Kewajiban</p>
          <Row label="Hutang Pembelian (Sisa DP)" value={data.kewajiban.hutang_pembelian} indent testid="neraca-hutang" />
          <div className="flex items-center justify-between py-3 mt-2 border-t border-[#26262E]">
            <span className="font-semibold text-zinc-100">Total Kewajiban</span>
            <Money value={data.kewajiban.total} className="font-semibold text-zinc-100" />
          </div>
        </div>
        <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4 fade-up" style={{ animationDelay: "160ms" }}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-500 mb-4">Ekuitas</p>
          <Row label="Modal" value={data.ekuitas.modal} indent testid="neraca-modal" />
          <Row label="Laba Ditahan" value={data.ekuitas.laba_ditahan} indent testid="neraca-laba-ditahan" />
          <div className="flex items-center justify-between py-3 mt-2 border-t border-[#26262E]">
            <span className="font-semibold text-zinc-100">Total Ekuitas</span>
            <Money value={data.ekuitas.total} className="font-semibold text-zinc-100" />
          </div>
        </div>
        <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-4 fade-up flex items-center justify-between" style={{ animationDelay: "240ms" }}>
          <span className="font-heading font-extrabold text-zinc-50">KEWAJIBAN + EKUITAS</span>
          <Money value={data.kewajiban.total + data.ekuitas.total} className="text-lg font-semibold text-blue-500" />
        </div>
      </div>
      </div>
    </div>
  );
}

function LabaPerMobil({ branch }) {
  const [rows, setRows] = useState(null);
  const [period, setPeriod] = useState(defaultPeriod());
  useEffect(() => {
    const params = new URLSearchParams();
    if (branch !== "all") params.set("branch_id", branch);
    const { start, end } = periodRange(period);
    if (start) {
      params.set("start", start);
      params.set("end", end);
    }
    api.get(`/reports/laba-per-mobil?${params}`).then((r) => setRows(r.data));
  }, [branch, period]);

  const tot = (k) => (rows || []).reduce((a, r) => a + (r[k] || 0), 0);
  const exportExcel = () => downloadCsv(`laba-per-mobil-${new Date().toISOString().slice(0, 10)}.csv`,
    ["No", "Mobil", "Plat", "Sales", "Harga Beli", "Biaya", "Komisi", "Harga Jual", "Laba", "Tanggal Terjual"],
    rows.map((r, i) => [i + 1, r.car_name, r.plate, r.salesperson || "-", r.harga_beli, r.total_biaya, r.komisi, r.harga_jual, r.laba, r.sold_date]));

  return (
    <div>
      <div className="print-hide mb-5 flex flex-wrap items-center gap-3">
        <PeriodFilter value={period} onChange={setPeriod} testidPrefix="lpm" />
        <ExportButtons prefix="lpm" onExcel={exportExcel} disabled={!rows || rows.length === 0} />
      </div>
      {rows && rows.length > 0 && (
        <div className="hidden print:block print-area" data-testid="laba-per-mobil-print">
          <PrintHeader subtitle="Laporan Laba per Mobil" />
          <table className="w-full text-[11px] border-collapse">
            <thead><tr><th className={`${pTh} w-7`}>No</th><th className={`${pTh} text-left`}>Mobil</th><th className={`${pTh} text-right`}>Harga Beli</th><th className={`${pTh} text-right`}>Biaya</th><th className={`${pTh} text-right`}>Komisi</th><th className={`${pTh} text-right`}>Harga Jual</th><th className={`${pTh} text-right`}>Laba</th><th className={pTh}>Terjual</th></tr></thead>
            <tbody>
              {rows.map((r, i) => <tr key={r.car_id}><td className={`${pTh} text-center`}>{i + 1}</td><td className={pTh}>{r.car_name}{r.salesperson ? ` · ${r.salesperson}` : ""}</td><td className={`${pTh} text-right`}>{fmtIDR(r.harga_beli)}</td><td className={`${pTh} text-right`}>{fmtIDR(r.total_biaya)}</td><td className={`${pTh} text-right`}>{fmtIDR(r.komisi)}</td><td className={`${pTh} text-right`}>{fmtIDR(r.harga_jual)}</td><td className={`${pTh} text-right`}>{fmtIDR(r.laba)}</td><td className={pTh}>{fmtDate(r.sold_date)}</td></tr>)}
              <tr><td colSpan={2} className={`${pTh} font-bold`}>TOTAL ({rows.length} unit)</td><td className={`${pTh} text-right font-bold`}>{fmtIDR(tot("harga_beli"))}</td><td className={`${pTh} text-right font-bold`}>{fmtIDR(tot("total_biaya"))}</td><td className={`${pTh} text-right font-bold`}>{fmtIDR(tot("komisi"))}</td><td className={`${pTh} text-right font-bold`}>{fmtIDR(tot("harga_jual"))}</td><td className={`${pTh} text-right font-bold`}>{fmtIDR(tot("laba"))}</td><td className={pTh}></td></tr>
            </tbody>
          </table>
        </div>
      )}
      {!rows ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : rows.length === 0 ? (
        <EmptyState icon={Car} title="Belum ada mobil terjual" desc="Laba per mobil muncul setelah ada transaksi penjualan pada periode ini" />
      ) : (
    <div className="print-hide bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up" data-testid="laba-per-mobil-table">
      <table className="w-full text-sm min-w-[860px]">
        <thead>
          <tr className="border-b border-[#1A1A22] text-left">
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Mobil</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Harga Beli</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Biaya</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Komisi</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Harga Jual</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Laba</th>
            <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Terjual</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.car_id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
              <td className="px-4 py-3 font-medium text-zinc-100">{r.car_name}
                <span className="block text-xs text-zinc-500">{r.plate} {r.salesperson ? `· Sales: ${r.salesperson}` : ""}</span>
              </td>
              <td className="px-4 py-3 text-right"><Money value={r.harga_beli} className="text-zinc-300" /></td>
              <td className="px-4 py-3 text-right"><Money value={r.total_biaya} className="text-zinc-300" /></td>
              <td className="px-4 py-3 text-right"><Money value={r.komisi} className="text-zinc-300" /></td>
              <td className="px-4 py-3 text-right"><Money value={r.harga_jual} className="text-zinc-100" /></td>
              <td className="px-4 py-3 text-right"><Money value={r.laba} className={`font-semibold ${r.laba >= 0 ? "text-emerald-500" : "text-rose-500"}`} /></td>
              <td className="px-4 py-3 text-zinc-400">{fmtDate(r.sold_date)}</td>
            </tr>
          ))}
          <tr className="bg-[#050505]/60" data-testid="lpm-total-row">
            <td className="px-4 py-3 font-semibold text-zinc-100">TOTAL ({rows.length} unit)</td>
            <td className="px-4 py-3 text-right"><Money value={tot("harga_beli")} className="font-semibold text-blue-500" /></td>
            <td className="px-4 py-3 text-right"><Money value={tot("total_biaya")} className="font-semibold text-blue-500" /></td>
            <td className="px-4 py-3 text-right"><Money value={tot("komisi")} className="font-semibold text-blue-500" /></td>
            <td className="px-4 py-3 text-right"><Money value={tot("harga_jual")} className="font-semibold text-blue-500" /></td>
            <td className="px-4 py-3 text-right"><Money value={tot("laba")} className={`font-semibold ${tot("laba") >= 0 ? "text-emerald-500" : "text-rose-500"}`} /></td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
      )}
    </div>
  );
}

function Persediaan({ branch }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams();
    if (branch !== "all") params.set("branch_id", branch);
    api.get(`/reports/persediaan?${params}`).then((r) => setRows(r.data));
  }, [branch]);

  const tot = (k) => (rows || []).reduce((a, r) => a + (r[k] || 0), 0);
  const btnCls = "h-10 px-4 inline-flex items-center gap-2 rounded-md border border-[#26262E] text-sm font-medium text-zinc-300 hover:bg-[#121218] transition-colors duration-200 disabled:opacity-40";

  const exportExcel = () => {
    downloadCsv(
      `laporan-persediaan-${new Date().toISOString().slice(0, 10)}.csv`,
      ["No", "Mobil", "Plat", "Cabang", "Status", "Tanggal Beli", "Harga Beli", "Total Biaya", "Modal", "DP Diterima", "Nilai Persediaan", "Harga Jual"],
      rows.map((r, i) => [i + 1, r.car_name, r.plate, r.branch_name, STATUS[r.status]?.label || r.status, r.purchase_date || "-", r.harga_beli, r.total_biaya, r.modal, r.dp_diterima, r.nilai_persediaan, r.harga_jual])
    );
  };

  const thCls = "border border-zinc-400 px-1.5 py-1";
  const pCell = (v) => fmtIDR(v || 0);

  return (
    <div>
      <div className="print-hide flex items-center gap-2 mb-5">
        <button data-testid="persediaan-export-excel" onClick={exportExcel} className={btnCls} disabled={!rows || rows.length === 0}>
          <Download className="w-4 h-4" /> Excel
        </button>
        <button data-testid="persediaan-export-pdf" onClick={() => window.print()} className={btnCls} disabled={!rows || rows.length === 0}>
          <Download className="w-4 h-4" /> PDF
        </button>
      </div>
      {!rows ? (
        <div className="print-hide h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="print-hide"><EmptyState icon={Car} title="Stok kosong" desc="Belum ada mobil dalam persediaan" /></div>
      ) : (
        <>
          <div className="print-hide bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up" data-testid="persediaan-table">
            <table className="w-full text-sm min-w-[1100px]">
              <thead>
                <tr className="border-b border-[#1A1A22] text-left">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Mobil</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Cabang</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tgl Beli</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Harga Beli</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Total Biaya</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Modal</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">DP Diterima</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Nilai Persediaan</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Harga Jual</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.car_id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                    <td className="px-4 py-3 font-medium text-zinc-100">{r.car_name}
                      <span className="block text-xs text-zinc-500">{r.plate}{r.konsinyasi ? " · Konsinyasi" : ""}</span>
                    </td>
                    <td className="px-4 py-3 text-zinc-400">{r.branch_name}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-zinc-400">{r.purchase_date ? fmtDate(r.purchase_date) : "-"}</td>
                    <td className="px-4 py-3 text-right"><Money value={r.harga_beli} className="text-zinc-300" /></td>
                    <td className="px-4 py-3 text-right"><Money value={r.total_biaya} className="text-zinc-300" /></td>
                    <td className="px-4 py-3 text-right"><Money value={r.modal} className="text-zinc-300" /></td>
                    <td className="px-4 py-3 text-right">{r.dp_diterima ? <Money value={r.dp_diterima} className="text-amber-400" /> : <span className="text-zinc-600">-</span>}</td>
                    <td className="px-4 py-3 text-right"><Money value={r.nilai_persediaan} className="text-zinc-100" /></td>
                    <td className="px-4 py-3 text-right"><Money value={r.harga_jual} className="text-zinc-100" /></td>
                  </tr>
                ))}
                <tr className="bg-[#050505]/60" data-testid="persediaan-total-row">
                  <td className="px-4 py-3 font-semibold text-zinc-100" colSpan={4}>TOTAL ({rows.length} unit)</td>
                  <td className="px-4 py-3 text-right"><Money value={tot("harga_beli")} className="font-semibold text-blue-500" /></td>
                  <td className="px-4 py-3 text-right"><Money value={tot("total_biaya")} className="font-semibold text-blue-500" /></td>
                  <td className="px-4 py-3 text-right"><Money value={tot("modal")} className="font-semibold text-blue-500" /></td>
                  <td className="px-4 py-3 text-right"><Money value={tot("dp_diterima")} className="font-semibold text-amber-400" /></td>
                  <td className="px-4 py-3 text-right"><Money value={tot("nilai_persediaan")} className="font-semibold text-blue-500" /></td>
                  <td className="px-4 py-3 text-right"><Money value={tot("harga_jual")} className="font-semibold text-blue-500" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="hidden print:block print-area" data-testid="persediaan-print">
            <PrintHeader subtitle="Laporan Persediaan Mobil" />
            <table className="w-full text-[11px] border-collapse">
              <thead>
                <tr>
                  <th className={`${thCls} text-center w-7`}>No</th>
                  <th className={`${thCls} text-left`}>Jenis Kendaraan</th>
                  <th className={`${thCls} text-left`}>Cabang</th>
                  <th className={`${thCls} text-left`}>Status</th>
                  <th className={`${thCls} text-right`}>Harga Beli</th>
                  <th className={`${thCls} text-right`}>Total Biaya</th>
                  <th className={`${thCls} text-right`}>Modal</th>
                  <th className={`${thCls} text-right`}>DP Diterima</th>
                  <th className={`${thCls} text-right`}>Nilai Persediaan</th>
                  <th className={`${thCls} text-right`}>Harga Jual</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.car_id}>
                    <td className={`${thCls} text-center`}>{i + 1}</td>
                    <td className={thCls}>{r.car_name}{r.plate ? ` (${r.plate})` : ""}</td>
                    <td className={thCls}>{r.branch_name}</td>
                    <td className={thCls}>{STATUS[r.status]?.label || r.status}{r.konsinyasi ? " · Konsinyasi" : ""}</td>
                    <td className={`${thCls} text-right`}>{pCell(r.harga_beli)}</td>
                    <td className={`${thCls} text-right`}>{pCell(r.total_biaya)}</td>
                    <td className={`${thCls} text-right`}>{pCell(r.modal)}</td>
                    <td className={`${thCls} text-right`}>{r.dp_diterima ? pCell(r.dp_diterima) : "-"}</td>
                    <td className={`${thCls} text-right`}>{pCell(r.nilai_persediaan)}</td>
                    <td className={`${thCls} text-right`}>{pCell(r.harga_jual)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} className={`${thCls} font-bold`}>TOTAL ({rows.length} unit)</td>
                  <td className={`${thCls} text-right font-bold`}>{pCell(tot("harga_beli"))}</td>
                  <td className={`${thCls} text-right font-bold`}>{pCell(tot("total_biaya"))}</td>
                  <td className={`${thCls} text-right font-bold`}>{pCell(tot("modal"))}</td>
                  <td className={`${thCls} text-right font-bold`}>{pCell(tot("dp_diterima"))}</td>
                  <td className={`${thCls} text-right font-bold`}>{pCell(tot("nilai_persediaan"))}</td>
                  <td className={`${thCls} text-right font-bold`}>{pCell(tot("harga_jual"))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("all");

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)); }, []);

  return (
    <div data-testid="reports-page">
      <PageHeader title="Laporan & Analisa" subtitle="Analisa bisnis, laba rugi, neraca & laba per unit">
        {user?.role !== "admin_cabang" && (
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger data-testid="report-branch-filter" className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
              <SelectItem value="all">Semua Cabang</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </PageHeader>

      <Tabs defaultValue="analisa">
        <TabsList className="print-hide bg-[#0A0A0C] border border-[#1A1A22] mb-6 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-analisa" value="analisa" className="data-[state=active]:bg-blue-500 data-[state=active]:text-zinc-950">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Analisa Bisnis
          </TabsTrigger>
          <TabsTrigger data-testid="tab-laba-rugi" value="laba-rugi" className="data-[state=active]:bg-blue-500 data-[state=active]:text-zinc-950">
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Laba Rugi
          </TabsTrigger>
          <TabsTrigger data-testid="tab-neraca" value="neraca" className="data-[state=active]:bg-blue-500 data-[state=active]:text-zinc-950">
            <Scale className="w-3.5 h-3.5 mr-1.5" /> Neraca
          </TabsTrigger>
          <TabsTrigger data-testid="tab-laba-mobil" value="laba-mobil" className="data-[state=active]:bg-blue-500 data-[state=active]:text-zinc-950">
            <Car className="w-3.5 h-3.5 mr-1.5" /> Laba per Mobil
          </TabsTrigger>
          {can(user, "lihat_hpp") && (
            <TabsTrigger data-testid="tab-persediaan" value="persediaan" className="data-[state=active]:bg-blue-500 data-[state=active]:text-zinc-950">
              <Boxes className="w-3.5 h-3.5 mr-1.5" /> Persediaan
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="analisa"><Analisa branch={branch} showHpp={can(user, "lihat_hpp")} /></TabsContent>
        <TabsContent value="laba-rugi"><LabaRugi branch={branch} /></TabsContent>
        <TabsContent value="neraca"><Neraca branch={branch} /></TabsContent>
        <TabsContent value="laba-mobil"><LabaPerMobil branch={branch} /></TabsContent>
        {can(user, "lihat_hpp") && <TabsContent value="persediaan"><Persediaan branch={branch} /></TabsContent>}
      </Tabs>
    </div>
  );
}
